import { Inject, Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
} from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import { catchError, Observable, switchMap, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { decodeJWT } from 'aws-amplify/auth';
import { HttpClient } from '@angular/common/http';
import { UAMAuthService } from '../services/uam-auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthStateService } from '../services/auth-state.service';

@Injectable({
  providedIn: 'root',
})
export class HttpApiInterceptor implements HttpInterceptor {
  idToken: string;
  refreshToken: string;
  constructor(
    private authService: UAMAuthService,
    private _httpClient: HttpClient,
    private _snackBar: MatSnackBar,
    private authStateService: AuthStateService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.idToken = authService.getIdToken();
    this.refreshToken = authService.getRefreshToken();
  }

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const endPoint = environment.endpoint;
    const excludedUrls = [
      `${endPoint}auth/settings`,
      `${endPoint}check-code`,
      `https://warfighter-be-dev.cristlgov.com/check-code`,
      `https://warfighter-be-qa.cristlgov.com/check-code`,
      `https://warfighter-be-preprod.cristlgov.com/check-code`,
      `https://warfighter-be.cristlgov.com/check-code`,
      '/assets/template/colors.json',
      `https://dev-osd-warfighter-export-data-bucket.s3.us-gov-west-1.amazonaws.com`,
      `https://qa-osd-warfighter-export-data-bucket.s3.us-gov-west-1.amazonaws.com`,
      `https://preprod-osd-warfighter-export-data-bucket.s3.us-gov-west-1.amazonaws.com`,
      `https://prod-osd-warfighter-export-data-bucket.s3.us-gov-west-1.amazonaws.com`,
      `https://${environment.uploadBucketEnv}-osd-warfighter-export-data-bucket.s3.us-gov-west-1.amazonaws.com`,
      `https://prod-osd-warfighter-export-data-bucket.s3.us-gov-west-1.amazonaws.com`,
      `https://quad-safire-bucket-${environment.uploadBucketEnv}.s3.us-gov-west-1.amazonaws.com`,
      `https://full-proposal-safire-bucket-${environment.uploadBucketEnv}.s3.us-gov-west-1.amazonaws.com`,
      `https://govtconsult-safire-bucket-${environment.uploadBucketEnv}.s3.us-gov-west-1.amazonaws.com`,
      `https://fullgovt-safire-bucket-${environment.uploadBucketEnv}.s3.us-gov-west-1.amazonaws.com`,
      `https://industry-safire-bucket-${environment.uploadBucketEnv}.s3.us-gov-west-1.amazonaws.com`,
      `https://targetrfp-safire-bucket-${environment.uploadBucketEnv}.s3.us-gov-west-1.amazonaws.com`,
      `https://pavcon-supplier-info-prod.s3.us-gov-west-1.amazonaws.com`,
      `https://pavcon-supplier-info-dev.s3.us-gov-west-1.amazonaws.com`,
      `https://dimnd-${environment.uploadBucketEnv}.s3.us-gov-west-1.amazonaws.com`,
      `https://jide-${environment.uploadBucketEnv}.s3.us-gov-west-1.amazonaws.com`,
    ];
    const refreshUrl = [`${environment.refreshEndpoint}`];

    if (
      !this.authStateService.isAutoRefreshEnabled() ||
      excludedUrls.some((x) => req.url.includes(x)) ||
      refreshUrl.some((x) => req.url.includes(x))
    ) {
      return next.handle(req.clone());
    }

    this.idToken = this.authService.getIdToken();
    this.refreshToken = this.authService.getRefreshToken();

    if (!this.idToken || this.idToken.trim() === '') {
      return next.handle(req.clone());
    }

    let decodedToken;
    try {
      decodedToken = decodeJWT(this.idToken);
    } catch (error) {
      console.error('HttpApiInterceptor: Error decoding token', error);
      return next.handle(req.clone());
    }

    const tokenExpTime = decodedToken.payload.exp as number;
    const currTime = Math.floor(Date.now() / 1000);

    if (currTime > tokenExpTime) {
      if (!this.refreshToken || this.refreshToken.trim() === '') {
        console.warn(
          'HttpApiInterceptor: Token expired but no refresh token available'
        );
        return next.handle(req.clone());
      }

      const deviceKey = this.authService.getDeviceKey();
      if (!deviceKey || deviceKey.trim() === '') {
        console.warn(
          'HttpApiInterceptor: Token expired but no device key available'
        );
        return next.handle(req.clone());
      }

      return this.authService.refreshTokenFunction(this.refreshToken).pipe(
        catchError((error) => {
          if (error.error?.errorMessage === 'refresh expired') {
            const redirectUrl = environment.url.replace(/https?:\/\//i, '');
            setTimeout(() => {
              this._snackBar
                .open('Session expired, login required.', 'OK', {
                  duration: 5000,
                })
                .afterDismissed()
                .subscribe(() => {
                  this.document.location.href =
                    environment.uamUrl + 'auth/login/' + redirectUrl;
                });
            }, 2000);
            return throwError(() => error);
          }

          console.warn(
            'HttpApiInterceptor: Token refresh failed, proceeding with original request',
            error
          );
          const authReq = req.clone({
            setHeaders: {
              Authorization: `Bearer ${this.idToken}`,
            },
          });
          return next.handle(authReq);
        }),
        tap({
          next: (response: any) => {
            let newToken: string;
            if (response?.IdToken) {
              newToken = response.IdToken;
            } else if (response?.response?.AuthenticationResult?.IdToken) {
              newToken = response.response.AuthenticationResult.IdToken;
            } else {
              console.error(
                'HttpApiInterceptor: Invalid refresh token response structure',
                response
              );
              throw new Error(
                'Invalid refresh token response: IdToken not found'
              );
            }

            this.idToken = newToken;
            this.authService.setIdToken(newToken);
          },
          error: (error) => {
            console.error(
              'HttpApiInterceptor: Error refreshing token in tap',
              error
            );
          },
        }),
        switchMap(() => {
          if (!this.idToken) {
            console.error(
              'HttpApiInterceptor: No token available after refresh'
            );
            const redirectUrl = environment.url.replace(/https?:\/\//i, '');
            this._snackBar
              .open('Session expired, login required.', 'OK', {
                duration: 5000,
              })
              .afterDismissed()
              .subscribe(() => {
                this.document.location.href =
                  environment.uamUrl + 'auth/login/' + redirectUrl;
              });
            return throwError(
              () => new Error('No token available after refresh')
            );
          }

          const authReq = req.clone({
            setHeaders: {
              Authorization: `Bearer ${this.idToken}`,
            },
          });
          return next.handle(authReq);
        })
      );
    } else {
      const authReq = req.clone({
        setHeaders: {
          Authorization: `Bearer ${this.idToken}`,
        },
      });
      return next.handle(authReq);
    }
  }
}
